import {UserStatus, type User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import type {UserTemplate} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";
import type {OrganizationalUnit} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnit";
import type {OrganizationalUnitTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplate";
import {
    AMARON__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID,
    AmaronRoles,
} from "~/domainModels/workspaceSpecific/amaron";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {
    findWorkspaceUserByStoredReferenceId,
    getUserDisplayName,
    type PlatformUserDisplayDetails,
} from "~/utilities/utilities";

export function isAmaronAgentUser(user: User): boolean {
    return String(user.templateId) === String(AmaronRoles.Agent);
}

/**
 * Marks the Amaron Assigned Agent (Assigned To) column read-only.
 * Used so Agent-role users cannot reassign leads in edit panels or journey profile.
 */
export function applyAmaronAssignedAgentReadOnlyPolicy<
    TColumn extends {columnId: Uuid | string; isEditable?: boolean},
    TGroup extends {columns: Array<TColumn>},
>(columnGrouping: Array<TGroup>): Array<TGroup> {
    const assignedToColumnId = String(AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID);

    return columnGrouping.map((group) => ({
        ...group,
        columns: group.columns.map((col) => {
            if (String(col.columnId) === assignedToColumnId) {
                return {...col, isEditable: false};
            }
            return col;
        }),
    }));
}

function isAmaronJourneyAssignedToSet(raw: unknown): boolean {
    return raw !== null && raw !== undefined && (typeof raw !== "string" || raw.trim() !== "");
}

/** True when the journey has an Assigned Agent value on the Assigned To column. */
export function isAmaronJourneyAssignedAgentSet(
    additionalColumnValues: Record<string, unknown>,
): boolean {
    return isAmaronJourneyAssignedToSet(
        additionalColumnValues[AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID],
    );
}

/** Sets Assigned On when an Amaron journey moves from unassigned to assigned agent. */
export function buildAmaronJourneyAssignedOnColumnUpdateWhenAssigningAgent(args: {
    previousAdditionalColumnValues: Record<string, unknown>;
    nextAdditionalColumnValues: Record<string, unknown>;
    assignedOnIso?: string;
}): Record<string, unknown> {
    const wasUnassigned =
        isAmaronJourneyAssignedToSet(
            args.previousAdditionalColumnValues[AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID],
        ) !== true;
    const isNowAssigned = isAmaronJourneyAssignedToSet(
        args.nextAdditionalColumnValues[AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID],
    );
    if (wasUnassigned !== true || isNowAssigned !== true) {
        return {};
    }
    return {
        [AMARON__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID]: args.assignedOnIso ?? new Date().toISOString(),
    };
}

function isActiveWorkspaceUser(user: User): boolean {
    return user.status === UserStatus.active;
}

/** Active Agent-role users for Assigned Agent pickers (no OU scoping). */
export function filterAmaronAgentUsersForAssignedToPicker(params: {
    users: Array<User>;
    currentAssigneeId: Uuid | null | undefined;
}): Array<User> {
    const {users, currentAssigneeId} = params;
    const eligible = users.filter(
        (user) => isActiveWorkspaceUser(user) === true && isAmaronAgentUser(user) === true,
    );
    const resolvedCurrentAssigneeId =
        currentAssigneeId === null || currentAssigneeId === undefined
            ? null
            : (() => {
                  const trimmed = String(currentAssigneeId).trim();
                  if (trimmed === "") {
                      return null;
                  }
                  const userById = users.find((user) => user.userId === currentAssigneeId);
                  if (userById !== undefined) {
                      return userById.userId;
                  }
                  const userByReference = findWorkspaceUserByStoredReferenceId(trimmed, users);
                  if (userByReference !== undefined) {
                      return userByReference.userId;
                  }
                  return null;
              })();

    if (
        resolvedCurrentAssigneeId !== null
        && eligible.some((user) => user.userId === resolvedCurrentAssigneeId) !== true
    ) {
        const currentAssignee = users.find((user) => user.userId === resolvedCurrentAssigneeId);
        if (currentAssignee !== undefined) {
            return [currentAssignee, ...eligible];
        }
    }

    return eligible;
}

export function buildAmaronAssignedAgentSelectOptions(params: {
    users: Array<User>;
    userTemplates: Array<UserTemplate>;
    organizationalUnits: Array<OrganizationalUnit>;
    organizationalUnitTemplates: Array<OrganizationalUnitTemplate>;
    pincodeOrganizationalUnitId: Uuid | null;
    currentAssigneeId: Uuid | null;
    platformUserDetailsByUserId?: ReadonlyMap<Uuid, PlatformUserDisplayDetails>;
}): Array<{label: string; value: Uuid}> {
    const eligibleUsers = filterAmaronAgentUsersForAssignedToPicker({
        users: params.users,
        currentAssigneeId: params.currentAssigneeId,
    });
    const byName = [...eligibleUsers].sort((a, b) => {
        const nameA = getUserDisplayName(a.userId, params.users, params.userTemplates, params.platformUserDetailsByUserId).toLowerCase();
        const nameB = getUserDisplayName(b.userId, params.users, params.userTemplates, params.platformUserDetailsByUserId).toLowerCase();
        return nameA.localeCompare(nameB);
    });

    return byName.map((user) => ({
        label: getUserDisplayName(user.userId, params.users, params.userTemplates, params.platformUserDetailsByUserId),
        value: user.userId,
    }));
}
